import { axiosInstance } from "@/lib/axios"
import { toast } from "@/components/ui/use-toast"
import { COURSE_STATUS } from "@/lib/constants/course"
import { createPendingOrder } from "./api"
import type { CourseDetail } from "@/lib/types/course/detail"
import { authService, IAuthResponse } from "./auth"

interface ICourseActionResponse {
  success: boolean
  message?: string
  redirectUrl?: string
  data?: any
}

export const courseService = {
  async addToCart(courseId: string): Promise<ICourseActionResponse> {
    const authCheck = authService.requireAuth()
    if (!authCheck.success) {
      return authCheck
    }

    try {
      const res = await axiosInstance.post("/api/carts", { course_id: courseId })
      if (res.status === 200) {
        return {
          success: true,
          message: res.data.message,
          redirectUrl: "/carts"
        }
      }
      return {
        success: false,
        message: res.data.message
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to add course to cart"
      }
    }
  },

  async getLastLesson(courseId: string, courseSlug: string): Promise<ICourseActionResponse> {
    const authCheck = authService.requireAuth()
    if (!authCheck.success) {
      return authCheck
    }

    try {
      const response = await axiosInstance.get(`/api/course/get-last-lesson/${courseId}`)
      const lastLessonId = response.data.last_lesson_id
      const lessonResponse = await axiosInstance.get(`/api/course/lesson/${lastLessonId}/${courseId}`)
      
      return {
        success: true,
        redirectUrl: `/course/learning/${courseSlug}/?lesson_id=${lessonResponse.data.data.id}`
      }
    } catch (error) {
      console.error("Error fetching lesson:", error)
      return {
        success: false,
        message: "Không thể truy cập bài học. Vui lòng thử lại sau."
      }
    }
  },

  async toggleWhitelist(courseId: string, isWhitelisted: boolean, courseName: string): Promise<ICourseActionResponse> {
    const authCheck = authService.requireAuth()
    if (!authCheck.success) {
      return authCheck
    }

    try {
      const res = await axiosInstance.post("/api/whitelist", {
        type: "course",
        item_id: courseId,
        is_active: isWhitelisted ? 0 : 1,
      })

      if (res.status === 200) {
        const message = isWhitelisted
          ? `Đã xóa khóa học "${courseName}" khỏi danh sách yêu thích của bạn`
          : `Đã thêm khóa học "${courseName}" vào danh sách yêu thích của bạn`

        return {
          success: true,
          message,
          data: { isWhitelisted: !isWhitelisted }
        }
      }
      return {
        success: false,
        message: "Không thể cập nhật danh sách yêu thích"
      }
    } catch (error) {
      return {
        success: false,
        message: "Không thể cập nhật danh sách yêu thích. Vui lòng thử lại sau."
      }
    }
  },

  async handleCourseAction(
    course: CourseDetail,
    setPendingCourse: (courseId: string) => void
  ): Promise<ICourseActionResponse> {
    // For these statuses, we need authentication
    if (([COURSE_STATUS.REGISTER, COURSE_STATUS.ENROLL, COURSE_STATUS.WAITING_FOR_PAYMENT] as number[]).includes(Number(course.status.status))) {
      const authCheck = authService.requireAuth()
      if (!authCheck.success) {
        setPendingCourse(course.id.toString())
        return authCheck
      }
    }
console.log(course.status.status)
    switch (course.status.status) {
      case COURSE_STATUS.REGISTER:
      case COURSE_STATUS.ENROLL:
        return await this.addToCart(course.id.toString())

      case COURSE_STATUS.CONTINUE_LEARNING:
      case COURSE_STATUS.GIFTED:
        return await this.getLastLesson(course.id.toString(), course.slug)

      case COURSE_STATUS.WAITING_FOR_PAYMENT:
        return {
          success: true,
          redirectUrl: "/carts"
        }

      case COURSE_STATUS.PURCHASING:
        try {
          const response = await createPendingOrder(Number(course.id))
          if (response.order_id) {
            return {
              success: true,
              redirectUrl: `/auth/orders/${response.order_id}/confirm`
            }
          }
          return {
            success: false,
            message: "Không thể xem khi chưa được xác nhận"
          }
        } catch (error: any) {
          if (error?.response?.status === 404) {
            return {
              success: false,
              redirectUrl: window.location.href
            }
          }
          return {
            success: false,
            message: "Không thể xem khi chưa được xác nhận"
          }
        }

      default:
        return {
          success: false,
          message: "Invalid course status"
        }
    }
  }
} 